/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type {
  TraceAttempt,
  TraceConnection,
  TraceEnvelope,
  TraceSession,
  StoredTraceEvent,
  StoredTraceSession,
  TraceUpload,
} from "./types";
import { TRACE_VERSION } from "./types";

const DATABASE_NAME = "element-call-call-trace-v1";
const DATABASE_VERSION = 2;
const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_EVENTS = 10_000;
const MAX_BYTES = 10 * 1024 * 1024;
const RESERVED_IMPORTANT_BYTES = 256 * 1024;

const requestResult = async <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void =>
      reject(request.error ?? new Error("Ошибка IndexedDB"));
  });

const transactionDone = async (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = (): void => resolve();
    transaction.onerror = (): void =>
      reject(transaction.error ?? new Error("Ошибка транзакции IndexedDB"));
    transaction.onabort = (): void =>
      reject(transaction.error ?? new Error("Транзакция IndexedDB отменена"));
  });

export interface LeasedTraceBatch {
  upload: TraceUpload;
  envelope: TraceEnvelope;
}

const toWireEvent = (
  event: StoredTraceEvent,
): TraceEnvelope["events"][number] => {
  const {
    deliveryState: _deliveryState,
    approximateBytes: _approximateBytes,
    ...wireEvent
  } = event;
  // Связь discriminator с payload уже проверена публичным record API.
  return wireEvent as TraceEnvelope["events"][number];
};

export const toWireTraceSession = (
  session: StoredTraceSession,
): TraceSession => {
  const {
    createdAt: _createdAt,
    lastEventAt: _lastEventAt,
    approximateBytes: _approximateBytes,
    ...wireSession
  } = session;
  return wireSession;
};

const relevantAttempts = (
  attempts: TraceAttempt[],
  events: TraceEnvelope["events"],
): TraceAttempt[] => {
  const attemptIds = new Set<string>();
  for (const event of events) {
    if (event.attemptId) attemptIds.add(event.attemptId);
    if (event.parentAttemptId) attemptIds.add(event.parentAttemptId);
  }
  return attempts.filter((attempt) => attemptIds.has(attempt.attemptId));
};

const relevantConnections = (
  connections: TraceConnection[],
  events: TraceEnvelope["events"],
): TraceConnection[] => {
  const connectionIds = new Set(
    events
      .map((event) => event.connectionId)
      .filter((connectionId): connectionId is string => connectionId !== null),
  );
  return connections.filter((connection) =>
    connectionIds.has(connection.connectionId),
  );
};

/** Возвращает точный размер сериализованного HTTP envelope в байтах. */
export const traceEnvelopeBytes = (envelope: TraceEnvelope): number =>
  new TextEncoder().encode(JSON.stringify(envelope)).byteLength;

/** Репозиторий append-only журнала и локального outbox. */
export class TraceRepository {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private async open(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = (): void => {
        const db = request.result;
        // Контракт v1 ещё не опубликован, поэтому старую локальную структуру
        // намеренно пересоздаём без миграции несовместимых snapshot.
        for (const storeName of Array.from(db.objectStoreNames)) {
          db.deleteObjectStore(storeName);
        }
        const sessions = db.createObjectStore("trace_sessions", {
          keyPath: "sessionId",
        });
        sessions.createIndex("startedAt", "startedAt");
        const attempts = db.createObjectStore("trace_attempts", {
          keyPath: "attemptId",
        });
        attempts.createIndex("sessionId", "sessionId");
        const connections = db.createObjectStore("trace_connections", {
          keyPath: "connectionId",
        });
        connections.createIndex("sessionId", "sessionId");
        connections.createIndex("attemptId", "attemptId");
        const events = db.createObjectStore("trace_events", {
          keyPath: "eventId",
        });
        events.createIndex("sessionSequence", ["sessionId", "sequence"], {
          unique: true,
        });
        events.createIndex("deliveryTimestamp", ["deliveryState", "timestamp"]);
        const uploads = db.createObjectStore("trace_uploads", {
          keyPath: "uploadId",
        });
        uploads.createIndex("stateNextAttempt", ["state", "nextAttemptAt"]);
      };
      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void =>
        reject(
          request.error ?? new Error("Не удалось открыть IndexedDB телеметрии"),
        );
    });
    return this.dbPromise;
  }

  /** Создаёт session и закрывает незавершённые session предыдущих запусков. */
  public async initializeSession(session: StoredTraceSession): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction(
      ["trace_sessions", "trace_events", "trace_uploads"],
      "readwrite",
    );
    const store = transaction.objectStore("trace_sessions");
    const previous = (await requestResult(
      store.getAll(),
    )) as StoredTraceSession[];
    const now = new Date().toISOString();
    for (const item of previous) {
      if (item.endedAt === null && item.sessionId !== session.sessionId) {
        store.put({
          ...item,
          projectionRevision: (item.projectionRevision ?? 0) + 1,
          endedAt: now,
          endReason: "superseded",
        });
      }
    }
    const eventStore = transaction.objectStore("trace_events");
    const previousEvents = (await requestResult(
      eventStore.getAll(),
    )) as StoredTraceEvent[];
    for (const event of previousEvents) {
      if (event.deliveryState === "leased")
        eventStore.put({ ...event, deliveryState: "pending" });
    }
    const uploadStore = transaction.objectStore("trace_uploads");
    const previousUploads = (await requestResult(
      uploadStore.getAll(),
    )) as TraceUpload[];
    for (const upload of previousUploads) {
      if (upload.state === "in_flight") {
        uploadStore.put({
          ...upload,
          state: "retry_wait",
          leaseOwner: null,
          leaseUntil: null,
        });
      }
    }
    store.add(session);
    await transactionDone(transaction);
    await this.prune(session.sessionId);
  }

  public async updateSession(
    sessionId: string,
    patch: Partial<StoredTraceSession>,
  ): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction("trace_sessions", "readwrite");
    const store = transaction.objectStore("trace_sessions");
    const session = (await requestResult(store.get(sessionId))) as
      | StoredTraceSession
      | undefined;
    if (session)
      store.put({
        ...session,
        ...patch,
        projectionRevision: (session.projectionRevision ?? 0) + 1,
      });
    await transactionDone(transaction);
  }

  /** Полностью очищает журнал при отключённом feature gate. */
  public async clearAll(): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction(
      [
        "trace_sessions",
        "trace_attempts",
        "trace_connections",
        "trace_events",
        "trace_uploads",
      ],
      "readwrite",
    );
    transaction.objectStore("trace_sessions").clear();
    transaction.objectStore("trace_attempts").clear();
    transaction.objectStore("trace_connections").clear();
    transaction.objectStore("trace_events").clear();
    transaction.objectStore("trace_uploads").clear();
    await transactionDone(transaction);
  }

  public async putAttempt(attempt: TraceAttempt): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction("trace_attempts", "readwrite");
    transaction.objectStore("trace_attempts").put(attempt);
    await transactionDone(transaction);
  }

  public async updateAttempt(
    attemptId: string,
    patch: Partial<TraceAttempt>,
  ): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction("trace_attempts", "readwrite");
    const store = transaction.objectStore("trace_attempts");
    const attempt = (await requestResult(store.get(attemptId))) as
      | TraceAttempt
      | undefined;
    if (attempt)
      store.put({
        ...attempt,
        ...patch,
        projectionRevision: (attempt.projectionRevision ?? 0) + 1,
      });
    await transactionDone(transaction);
  }

  public async putConnection(connection: TraceConnection): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction("trace_connections", "readwrite");
    transaction.objectStore("trace_connections").put(connection);
    await transactionDone(transaction);
  }

  public async updateConnection(
    connectionId: string,
    patch: Partial<TraceConnection>,
  ): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction("trace_connections", "readwrite");
    const store = transaction.objectStore("trace_connections");
    const connection = (await requestResult(store.get(connectionId))) as
      | TraceConnection
      | undefined;
    if (connection)
      store.put({
        ...connection,
        ...patch,
        projectionRevision: (connection.projectionRevision ?? 0) + 1,
      });
    await transactionDone(transaction);
  }

  /** Атомарно выделяет sequence и добавляет событие. */
  public async appendEvent(
    event: Omit<StoredTraceEvent, "sequence">,
  ): Promise<StoredTraceEvent> {
    const db = await this.open();
    const transaction = db.transaction(
      ["trace_sessions", "trace_events"],
      "readwrite",
    );
    const sessions = transaction.objectStore("trace_sessions");
    const events = transaction.objectStore("trace_events");
    const session = (await requestResult(sessions.get(event.sessionId))) as
      | StoredTraceSession
      | undefined;
    if (!session) {
      transaction.abort();
      throw new Error("Session телеметрии не найдена");
    }
    const saved = { ...event, sequence: session.lastSequence + 1 };
    events.add(saved);
    sessions.put({
      ...session,
      lastSequence: saved.sequence,
      lastEventAt: saved.timestamp,
      approximateBytes: session.approximateBytes + saved.approximateBytes,
    });
    await transactionDone(transaction);
    return saved;
  }

  public async pendingCount(): Promise<number> {
    const db = await this.open();
    const transaction = db.transaction("trace_events", "readonly");
    const events = (await requestResult(
      transaction.objectStore("trace_events").getAll(),
    )) as StoredTraceEvent[];
    return events.filter((event) => event.deliveryState === "pending").length;
  }

  public async bufferUsage(): Promise<{ events: number; bytes: number }> {
    const db = await this.open();
    const transaction = db.transaction("trace_events", "readonly");
    const events = (await requestResult(
      transaction.objectStore("trace_events").getAll(),
    )) as StoredTraceEvent[];
    return {
      events: events.length,
      bytes: events.reduce((sum, event) => sum + event.approximateBytes, 0),
    };
  }

  /** Выдаёт под lease одну ограниченную пачку, не смешивая разные session. */
  public async leasePendingBatch(
    owner: string,
    maxEvents: number,
    maxBytes: number,
  ): Promise<LeasedTraceBatch | null> {
    const db = await this.open();
    const transaction = db.transaction(
      [
        "trace_sessions",
        "trace_attempts",
        "trace_connections",
        "trace_events",
        "trace_uploads",
      ],
      "readwrite",
    );
    const eventStore = transaction.objectStore("trace_events");
    const allEvents = (await requestResult(
      eventStore.getAll(),
    )) as StoredTraceEvent[];
    const uploadStore = transaction.objectStore("trace_uploads");
    const existingUploads = (await requestResult(
      uploadStore.getAll(),
    )) as TraceUpload[];
    const retryUploads = existingUploads
      .filter(
        (upload) =>
          upload.state === "retry_wait" &&
          (upload.nextAttemptAt === null ||
            Date.parse(upload.nextAttemptAt) <= Date.now()),
      )
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    const retryUpload = retryUploads[0];
    if (retryUpload) {
      const selected = allEvents
        .filter(
          (event) =>
            event.sessionId === retryUpload.sessionId &&
            event.sequence >= retryUpload.firstSequence &&
            event.sequence <= retryUpload.lastSequence &&
            event.deliveryState === "pending",
        )
        .sort((a, b) => a.sequence - b.sequence);
      if (selected.length > 0) {
        const session = (await requestResult(
          transaction.objectStore("trace_sessions").get(retryUpload.sessionId),
        )) as StoredTraceSession;
        const allAttempts = (await requestResult(
          transaction
            .objectStore("trace_attempts")
            .index("sessionId")
            .getAll(retryUpload.sessionId),
        )) as TraceAttempt[];
        const allConnections = (await requestResult(
          transaction
            .objectStore("trace_connections")
            .index("sessionId")
            .getAll(retryUpload.sessionId),
        )) as TraceConnection[];
        const wireEvents = selected.map(toWireEvent);
        const envelope: TraceEnvelope = {
          traceVersion: TRACE_VERSION,
          uploadId: retryUpload.uploadId,
          session: toWireTraceSession(session),
          attempts: relevantAttempts(allAttempts, wireEvents),
          connections: relevantConnections(allConnections, wireEvents),
          events: wireEvents,
        };
        const now = new Date();
        const upload: TraceUpload = {
          ...retryUpload,
          bytes: traceEnvelopeBytes(envelope),
          state: "in_flight",
          attemptCount: retryUpload.attemptCount + 1,
          lastAttemptAt: now.toISOString(),
          nextAttemptAt: null,
          leaseOwner: owner,
          leaseUntil: new Date(now.getTime() + 30_000).toISOString(),
        };
        for (const event of selected)
          eventStore.put({ ...event, deliveryState: "leased" });
        uploadStore.put(upload);
        await transactionDone(transaction);
        return { upload, envelope };
      }
      uploadStore.put({
        ...retryUpload,
        state: "dead",
        errorCode: "events_missing",
      });
    }
    const reservedByRetry = existingUploads.filter(
      (upload) => upload.state === "retry_wait",
    );
    const pending = allEvents
      .filter(
        (event) =>
          event.deliveryState === "pending" &&
          !reservedByRetry.some(
            (upload) =>
              upload.sessionId === event.sessionId &&
              event.sequence >= upload.firstSequence &&
              event.sequence <= upload.lastSequence,
          ),
      )
      .sort(
        (a, b) =>
          a.timestamp.localeCompare(b.timestamp) || a.sequence - b.sequence,
      );
    if (pending.length === 0) {
      await transactionDone(transaction);
      return null;
    }

    const sessionId = pending[0].sessionId;
    const session = (await requestResult(
      transaction.objectStore("trace_sessions").get(sessionId),
    )) as StoredTraceSession;
    const allAttempts = (await requestResult(
      transaction
        .objectStore("trace_attempts")
        .index("sessionId")
        .getAll(sessionId),
    )) as TraceAttempt[];
    const allConnections = (await requestResult(
      transaction
        .objectStore("trace_connections")
        .index("sessionId")
        .getAll(sessionId),
    )) as TraceConnection[];
    const uploadId = crypto.randomUUID();
    const selected: StoredTraceEvent[] = [];
    let wireEvents: TraceEnvelope["events"] = [];
    let envelope: TraceEnvelope | null = null;
    let bytes = 0;
    for (const event of pending) {
      if (event.sessionId !== sessionId) continue;
      if (selected.length >= maxEvents) break;
      const candidateEvents = [...wireEvents, toWireEvent(event)];
      const candidateEnvelope: TraceEnvelope = {
        traceVersion: TRACE_VERSION,
        uploadId,
        session: toWireTraceSession(session),
        attempts: relevantAttempts(allAttempts, candidateEvents),
        connections: relevantConnections(allConnections, candidateEvents),
        events: candidateEvents,
      };
      const candidateBytes = traceEnvelopeBytes(candidateEnvelope);
      if (selected.length > 0 && candidateBytes > maxBytes) break;
      selected.push(event);
      wireEvents = candidateEvents;
      envelope = candidateEnvelope;
      bytes = candidateBytes;
    }

    // Одиночное событие не оставляем навсегда в outbox, даже если session metadata
    // неожиданно превысили клиентский лимит. Серверный лимит остаётся выше.
    if (!envelope) throw new Error("Не удалось сформировать пакет телеметрии");
    const now = new Date();
    const upload: TraceUpload = {
      uploadId,
      sessionId,
      firstSequence: selected[0].sequence,
      lastSequence: selected[selected.length - 1].sequence,
      eventCount: selected.length,
      bytes,
      state: "in_flight",
      attemptCount: 1,
      createdAt: now.toISOString(),
      lastAttemptAt: now.toISOString(),
      nextAttemptAt: null,
      httpStatus: null,
      errorCode: null,
      leaseOwner: owner,
      leaseUntil: new Date(now.getTime() + 30_000).toISOString(),
    };
    for (const event of selected)
      eventStore.put({ ...event, deliveryState: "leased" });
    uploadStore.add(upload);
    await transactionDone(transaction);

    return { upload, envelope };
  }

  public async acknowledge(upload: TraceUpload): Promise<void> {
    await this.finishUpload(
      upload,
      "acknowledged",
      "acknowledged",
      null,
      null,
      null,
    );
  }

  public async failUpload(
    upload: TraceUpload,
    dead: boolean,
    nextAttemptAt: string | null,
    httpStatus: number | null,
    errorCode: string,
  ): Promise<void> {
    await this.finishUpload(
      upload,
      dead ? "dead" : "retry_wait",
      dead ? "dead" : "pending",
      nextAttemptAt,
      httpStatus,
      errorCode,
    );
  }

  private async finishUpload(
    upload: TraceUpload,
    uploadState: TraceUpload["state"],
    deliveryState: StoredTraceEvent["deliveryState"],
    nextAttemptAt: string | null,
    httpStatus: number | null,
    errorCode: string | null,
  ): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction(
      ["trace_events", "trace_uploads"],
      "readwrite",
    );
    const eventStore = transaction.objectStore("trace_events");
    const allEvents = (await requestResult(
      eventStore.getAll(),
    )) as StoredTraceEvent[];
    for (const event of allEvents) {
      if (
        event.sessionId === upload.sessionId &&
        event.sequence >= upload.firstSequence &&
        event.sequence <= upload.lastSequence &&
        event.deliveryState === "leased"
      ) {
        eventStore.put({ ...event, deliveryState });
      }
    }
    transaction.objectStore("trace_uploads").put({
      ...upload,
      state: uploadState,
      nextAttemptAt,
      httpStatus,
      errorCode,
      leaseOwner: null,
      leaseUntil: null,
    });
    await transactionDone(transaction);
  }

  /** Удаляет просроченные и старейшие завершённые session. */
  private async prune(activeSessionId: string): Promise<void> {
    const db = await this.open();
    const transaction = db.transaction(
      [
        "trace_sessions",
        "trace_attempts",
        "trace_connections",
        "trace_events",
        "trace_uploads",
      ],
      "readwrite",
    );
    const sessionStore = transaction.objectStore("trace_sessions");
    const attemptStore = transaction.objectStore("trace_attempts");
    const connectionStore = transaction.objectStore("trace_connections");
    const eventStore = transaction.objectStore("trace_events");
    const uploadStore = transaction.objectStore("trace_uploads");
    const sessions = (await requestResult(
      sessionStore.getAll(),
    )) as StoredTraceSession[];
    const events = (await requestResult(
      eventStore.getAll(),
    )) as StoredTraceEvent[];
    const uploads = (await requestResult(
      uploadStore.getAll(),
    )) as TraceUpload[];
    const cutoff = Date.now() - RETENTION_MS;
    let totalBytes = events.reduce(
      (sum, event) => sum + event.approximateBytes,
      0,
    );
    let totalEvents = events.length;
    const removable = sessions
      .filter(
        (session) =>
          session.sessionId !== activeSessionId && session.endedAt !== null,
      )
      .sort((a, b) => {
        const aAck = events.some(
          (event) =>
            event.sessionId === a.sessionId &&
            event.deliveryState !== "acknowledged",
        )
          ? 1
          : 0;
        const bAck = events.some(
          (event) =>
            event.sessionId === b.sessionId &&
            event.deliveryState !== "acknowledged",
        )
          ? 1
          : 0;
        return aAck - bAck || a.startedAt.localeCompare(b.startedAt);
      });

    for (const session of removable) {
      const expired = Date.parse(session.startedAt) < cutoff;
      if (!expired && totalEvents <= MAX_EVENTS && totalBytes <= MAX_BYTES)
        break;
      sessionStore.delete(session.sessionId);
      for (const event of events.filter(
        (item) => item.sessionId === session.sessionId,
      )) {
        eventStore.delete(event.eventId);
        totalEvents -= 1;
        totalBytes -= event.approximateBytes;
      }
      const attempts = (await requestResult(
        attemptStore.index("sessionId").getAll(session.sessionId),
      )) as TraceAttempt[];
      for (const attempt of attempts) attemptStore.delete(attempt.attemptId);
      const connections = (await requestResult(
        connectionStore.index("sessionId").getAll(session.sessionId),
      )) as TraceConnection[];
      for (const connection of connections)
        connectionStore.delete(connection.connectionId);
      for (const upload of uploads.filter(
        (item) => item.sessionId === session.sessionId,
      )) {
        uploadStore.delete(upload.uploadId);
      }
    }
    await transactionDone(transaction);
  }

  public static readonly limits = {
    maxEvents: MAX_EVENTS,
    maxBytes: MAX_BYTES,
    reservedImportantBytes: RESERVED_IMPORTANT_BYTES,
  };
}
